import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'
import { useSocket } from '../context/SocketContext'

const rwf = (n) => `RWF ${Number(n ?? 0).toLocaleString()}`

// ── Date helpers ──────────────────────────────────────────────────────────────
const toISO  = (d) => d.toISOString().slice(0, 10)
const fmtLbl = (iso) => {
  const [y, m, day] = iso.split('-')
  return `${m}/${day}/${y}`
}

function buildPresets() {
  const now   = new Date()
  const y     = now.getFullYear()
  const mo    = now.getMonth()      // 0-based
  const today = toISO(now)

  const monthName = (year, month) =>
    new Date(year, month, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })

  const lastDayOf = (year, month) => new Date(year, month + 1, 0).getDate()

  return [
    {
      label: 'This month',
      from:  `${y}-${String(mo + 1).padStart(2, '0')}-01`,
      to:    today,
    },
    {
      label: monthName(y, mo - 1 < 0 ? 11 : mo - 1),
      from:  toISO(new Date(y, mo - 1, 1)),
      to:    toISO(new Date(y, mo - 1, lastDayOf(y, mo - 1))),
    },
    {
      label: monthName(y, mo - 2 < 0 ? 10 : mo - 2),
      from:  toISO(new Date(y, mo - 2, 1)),
      to:    toISO(new Date(y, mo - 2, lastDayOf(y, mo - 2))),
    },
  ]
}

// ── Sub-components ────────────────────────────────────────────────────────────
function MiniCard({ label, value, accent, change, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
      {change != null && (
        <div style={{ fontSize: 11, fontWeight: 700, color: change.up ? '#15803d' : '#b91c1c', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>
          {change.up ? '▲' : '▼'} {change.pct}% vs prev period
        </div>
      )}
      {sub && (
        <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 5, fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.5 }}>{sub}</div>
      )}
    </div>
  )
}

function BarChart({ data, label, color = '#E8440A' }) {
  if (!data?.length) return null
  const max = Math.max(...data.map(r => r.value), 1)
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 16 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100 }}>
        {data.map((r, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
            <div title={rwf(r.value)}
              style={{ width: '100%', background: color, borderRadius: '3px 3px 0 0', opacity: 0.85, transition: 'height 0.3s',
                height: `${Math.max(4, Math.round((r.value / max) * 80))}px` }} />
            <span style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap', maxWidth: '100%', textAlign: 'center', overflow: 'hidden' }}>{r.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Analytics() {
  const PRESETS = buildPresets()

  const [dateFrom,     setDateFrom]     = useState(PRESETS[0].from)
  const [dateTo,       setDateTo]       = useState(PRESETS[0].to)
  const [data,         setData]         = useState(null)
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState(false)
  const [activePreset, setActivePreset] = useState(0)
  const [hasNewData,   setHasNewData]   = useState(false)

  const { on } = useSocket()

  const applyPreset = (idx) => {
    setActivePreset(idx)
    setDateFrom(PRESETS[idx].from)
    setDateTo(PRESETS[idx].to)
  }

  const fetch = useCallback(() => {
    setLoading(true); setError(false); setHasNewData(false)
    api.get(`/analytics/shop?date_from=${dateFrom}&date_to=${dateTo}`)
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [dateFrom, dateTo])

  useEffect(() => { fetch() }, [fetch])

  // Flag when new orders come in so the user can choose to refresh
  useEffect(() => {
    const unsub = on('notification', (n) => {
      if (n.type === 'new_order') setHasNewData(true)
    })
    return unsub
  }, [on])

  const onFromChange = (e) => { setDateFrom(e.target.value); setActivePreset(null) }
  const onToChange   = (e) => { setDateTo(e.target.value);   setActivePreset(null) }

  const s          = data?.summary || {}
  const changePct  = s.revenue_change_pct != null ? { up: s.revenue_up, pct: s.revenue_change_pct } : null

  const INP = {
    height: 36, border: '1px solid #e9e9e4', borderRadius: 8, padding: '0 10px',
    fontSize: 13, outline: 'none', fontFamily: "'JetBrains Mono',monospace",
    color: '#111317', background: '#fff', cursor: 'pointer',
  }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Analytics</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Track your shop performance over time.</div>
      </div>

      {/* Date range filter */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>

          {/* From */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>From</div>
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={onFromChange}
              style={INP}
            />
          </div>

          {/* To */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>To</div>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={new Date().toISOString().slice(0, 10)}
              onChange={onToChange}
              style={INP}
            />
          </div>

          {/* Preset shortcuts */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignSelf: 'flex-end' }}>
            {PRESETS.map((p, i) => (
              <button key={i} onClick={() => applyPreset(i)}
                style={{
                  height: 36, padding: '0 14px', borderRadius: 8,
                  border: `1px solid ${activePreset === i ? '#111317' : '#e9e9e4'}`,
                  background: activePreset === i ? '#111317' : '#fff',
                  color: activePreset === i ? '#fff' : '#374151',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.15s',
                }}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Selected range summary */}
          {dateFrom && dateTo && (
            <div style={{ marginLeft: 'auto', fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", alignSelf: 'flex-end', paddingBottom: 8 }}>
              {fmtLbl(dateFrom)} → {fmtLbl(dateTo)}
            </div>
          )}
        </div>
      </div>

      {/* New data banner */}
      {hasNewData && !loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', flexShrink: 0, boxShadow: '0 0 0 3px rgba(22,163,74,0.2)' }} />
          <span style={{ fontWeight: 600, color: '#15803d', flex: 1 }}>New orders received — data may have changed.</span>
          <button onClick={fetch} style={{ height: 30, padding: '0 14px', borderRadius: 7, border: 'none', background: '#15803d', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            Refresh
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
          {[1,2,3,4].map(i => <div key={i} style={{ flex: 1, height: 100, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, opacity: 0.5 }} />)}
        </div>
      ) : error ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '20px', textAlign: 'center', color: '#b91c1c', marginBottom: 24 }}>
          Failed to load analytics. Try refreshing.
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
            <MiniCard label="Gross Revenue"  value={rwf(s.revenue)}       accent="#15803d" change={changePct} />
            <MiniCard
              label="Net Payout"
              value={rwf(s.total_payout ?? 0)}
              accent="#1d4ed8"
              sub={s.total_payout != null
                ? `−${rwf((s.ipay_fee ?? 0) + (s.platform_fee ?? 0))} fees · +${rwf(s.delivery_payout ?? 0)} delivery · +${rwf(s.tips_total ?? 0)} tips`
                : undefined}
            />
            <MiniCard label="Orders"        value={s.orders ?? 0}         accent="#111317" />
            <MiniCard label="Avg Order"     value={rwf(s.avg_order)}      accent="#111317" />
            <MiniCard label="Units Sold"    value={s.units_sold ?? 0}     accent="#E8440A" />
          </div>

          {/* Order status breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
            {[
              ['✅ Paid Orders',   s.paid_orders  ?? 0, '#15803d'],
              ['⏳ Unpaid Orders', s.unpaid_orders ?? 0, '#b45309'],
              ['🚚 Delivered',     s.delivered     ?? 0, '#111317'],
            ].map(([k, v, color]) => (
              <div key={k} style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{k}</span>
                <span style={{ fontSize: 18, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
            <BarChart data={data.revenue_chart} label="Revenue" color="#E8440A" />
            <BarChart data={data.orders_chart}  label="Orders"  color="#3b82f6" />
          </div>

          {/* Top products */}
          {data.top_products?.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #e9e9e4', fontSize: 13, fontWeight: 700 }}>Top Products</div>
              {data.top_products.map((p, i) => (
                <div key={i} style={{ padding: '12px 18px', borderBottom: i < data.top_products.length - 1 ? '1px solid #f3f3f0' : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 6, background: '#f9f9f7', border: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#9a9a94', flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{p.units_sold} units sold</div>
                  </div>
                  <div style={{ width: 80 }}>
                    <div style={{ height: 6, background: '#f3f3f0', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${p.pct}%`, background: '#E8440A', borderRadius: 3 }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d', minWidth: 110, textAlign: 'right' }}>
                    {rwf(p.revenue)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
